import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  CurrentUserWatchService,
  DatadogRumService,
  OrganisationSettingService,
  SettingsService,
  UserService,
} from '../../services';
import { LoggerService } from '../../services';
import { TaggedLogger } from '../../services';
import { MenuOrientation } from './menuOrientation';
import { MessageService, Toast } from 'primeng';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DialpadSettingsDTO } from '../dialpad/dialpad-settings';
import { OrganisationSettingEnum } from 'src/app/enums/organisation-settings';
import { RefinerSurveyService } from 'src/app/services/refiner-survey.service';
import { ProductAnalyticsService } from '../../services/product-analytics/product-analytics.service';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public renderer: Renderer2,
    private currentUserWatchService: CurrentUserWatchService,
    private primeNgMessageService: MessageService,
    private datadogRumService: DatadogRumService,
    private settingsService: SettingsService,
    private orgSettingService: OrganisationSettingService,
    private refinerSurveyService: RefinerSurveyService,
    private productAnalyticsService: ProductAnalyticsService,
    private userService: UserService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('component:app');
  }

  @Input() org_type: string; // todo is it an input, and where from? don't see it in the template

  private org_id: number;

  private routeChange$: Subscription;

  orgTypeId: number;

  enableLiveChat: boolean;

  layoutCompact: boolean = true;

  layoutMode: MenuOrientation = MenuOrientation.STATIC;

  darkMenu: boolean = true;

  profileMode: string = 'inline';

  rotateMenuButton: boolean;

  topbarMenuActive: boolean;

  overlayMenuActive: boolean;

  staticMenuDesktopInactive: boolean;

  staticMenuMobileActive: boolean;

  layoutContainer: HTMLDivElement;

  layoutMenuScroller: HTMLDivElement;

  menuClick: boolean;

  topbarItemClick: boolean;

  activeTopbarItem: any;

  documentClickListener: Function;

  resetMenu: boolean;

  searchMsgs: Array<string> = [];

  private logger: TaggedLogger;

  @ViewChild('layoutContainer') layourContainerViewChild: ElementRef;

  @ViewChild('layoutMenuScroller') layoutMenuScrollerViewChild: ElementRef;

  @ViewChild('mainGrowl') toast: Toast;

  toastPosition: string = 'top-right';

  dialpadSettings: DialpadSettingsDTO;
  settingsReady = false;

  async ngOnInit() {
    this.logger.trace('OnInit');
    this.currentUserWatchService.watch();
    this.addRouteChangeListener();
    let toastPosition = window.localStorage.getItem('preferred-toast-position');
    if (toastPosition) {
      this.toastPosition = toastPosition;
    }
    await this.datadogRumService.init();
    this.refinerSurveyService.setProject();
    await this.setupLiveChat();
    setTimeout(() => this.injectDialpadChatbot(), 100);
  }

  //Customer Info settings
  private dxWfvarsApplied = false;

  manipulateDialpadElement() {
    /**
     * If livechat is enabled, look for the dialpad component in the DOM.
     * If it is found, manipulate the minimised chat window to look like the original dialpad icon.
     */
    if (this.enableLiveChat) {
      // observe the body until the dialpad component is found.
      const body = document.querySelector('body');
      const bodyObserver = new MutationObserver(() => {
        var chatbotFound = this.observeBodyForDialpadChatWindow();
        if (chatbotFound) {
          // Disconnect the body observer when the dialpad component is found.
          bodyObserver.disconnect();
        }
      });
      // start body observer.
      bodyObserver.observe(body, {
        childList: true,
        subtree: false,
      });
      this.userService.isUserImpersonating().then((res) => {
        const email = res.isImpersonating ? res.originalUserEmail : res.impersonatedUserEmail;
        const username = res.isImpersonating ? res.originalUserName : res.impersonatedUserName;
        this.setupCustomerInfo(username, email);
      });
    }
  }

  private setupCustomerInfo(name: string, email: string) {
    if (!this.dxWfvarsApplied && this.enableLiveChat) {
      const fabImg =
        document.getElementById('dx_chatbot_fab_id') ||
        // fallback if id changes:
        document.querySelector('img[data-dxchannelid][data-dxprovemail]');

      if (fabImg instanceof HTMLImageElement) {
        fabImg.setAttribute(
          'data-dxwfvars',
          `customer.session.email=${email},customer.session.name=${name}`,
        );

        this.dxWfvarsApplied = true;
      }
    }
  }

  observeBodyForDialpadChatWindow(): boolean {
    /**
     * Monitors changes to the DOM looking for the dialpad chatbot element.
     * If the dialpad chatbot element is found, it creates an observer to monitor changes within the dialpad component.
     * @returns true when the dialpad chatbot element is found and its internal observer is created. Returns false
     */

    const chatbot = document.querySelector(
      'dialpad-chatbot-' + this.dialpadSettings.liveChatChannelId,
    );
    if (chatbot && chatbot.shadowRoot) {
      const shadowRoot = chatbot.shadowRoot;
      // Observe inside shadow DOM
      const observer = new MutationObserver(() => {
        this.changeIcon(shadowRoot);
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true,
      });

      // Try immediately in case it's already rendered
      this.changeIcon(shadowRoot);
      // dialpad component found and new observer created so return true.
      return true;
    }
    // dialpad component not found so return false.
    return false;
  }

  changeIcon(el: ShadowRoot) {
    /**
     * Use the shadowroot to manipulate the dialpad chatbot minimised window to look like the original dialpad icon.
     */
    const chatWindowMinimised = el.querySelector('.cb__minimized-screen') as HTMLElement;
    if (chatWindowMinimised && !chatWindowMinimised.classList.contains('custom-styled')) {
      const chatWindowFull = el.querySelector('.cb__chat-window') as HTMLElement;
      // force styling to match original
      chatWindowFull.style.setProperty('height', '56px', 'important');
      chatWindowFull.style.setProperty('width', '56px', 'important');
      chatWindowFull.style.setProperty('border-radius', '100%', 'important');
      chatWindowFull.style.setProperty('bottom', '30px');
      chatWindowFull.style.setProperty('right', '30px');
      chatWindowFull.style.setProperty('z-index', '123');

      // change icon image to match original
      const image = el.querySelector('img') as HTMLImageElement;
      image.src =
        'https://eu-west.dx.dialpad.com/kpd-static/providers/42943484/webchat/0ec4a0e18728430b8b0f4871332d0ba1/images/fab-icon.png';

      const section = chatWindowFull.querySelector('section') as HTMLElement;
      section.style.setProperty('background-color', 'rgb(41, 86, 127)', 'important');
      section.style.setProperty('height', '100%', 'important');

      // Remove text
      const span = el.querySelector('span') as HTMLElement;
      span.innerText = '';
    }
  }

  private async setupLiveChat() {
    const organisation = await this.userService.getCurrentUserOrg();
    const organisationSetting = await this.orgSettingService.search({
      setting: OrganisationSettingEnum.EnableLiveChat,
      organisationId: organisation.id,
      component: 'organisation-custom-settings',
    });
    const orgSetting = this.orgSettingService.parseSettings(organisationSetting);
    this.enableLiveChat =
      organisationSetting.length > 0 &&
      orgSetting[OrganisationSettingEnum.EnableLiveChat].intValue === 1
        ? true
        : false;
    this.dialpadSettings = await this.settingsService.GetDialpadLivechatSettings();
    this.settingsReady = true;
    this.manipulateDialpadElement();
  }

  injectDialpadChatbot() {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'https://eu-west.dx.dialpad.com/dxclient/dist/dialpad-chatbot.es.js';

    const configScript = document.createElement('script');
    // If this innerHTML script is changed in any way, you must recompute the sha-256 hash and add it to the Content Security Policy
    // See https://gitlab.com/engineius/app/engineius-app/-/wikis/Content-Security-Policy for more info.
    configScript.innerHTML = `window.dxe = { server: 'https://eu-west.dx.dialpad.com' };`;

    document.body.appendChild(configScript);
    document.body.appendChild(script);
  }

  ngOnDestroy() {
    this.logger.trace('OnDestroy');
    this.currentUserWatchService.stop();

    // from old OnDestory, previously was not working, so may not work correctly now?
    if (this.documentClickListener && typeof this.documentClickListener === 'function') {
      this.documentClickListener();
    }

    jQuery(this.layoutMenuScroller).nanoScroller({ flash: true });
  }

  ngAfterViewInit() {
    this.layoutContainer = <HTMLDivElement>this.layourContainerViewChild.nativeElement;
    this.layoutMenuScroller = <HTMLDivElement>this.layoutMenuScrollerViewChild.nativeElement;

    //hides the horizontal submenus or top menu if outside is clicked
    this.documentClickListener = this.renderer.listen('body', 'click', (event) => {
      if (!this.topbarItemClick) {
        this.activeTopbarItem = null;
        this.topbarMenuActive = false;
      }
      if (!this.menuClick && this.isHorizontal()) {
        this.resetMenu = true;
      }
      this.topbarItemClick = false;
      this.menuClick = false;
    });
    // todo: setTimeout seems ropey
    setTimeout(() => {
      jQuery(this.layoutMenuScroller).nanoScroller({ flash: true });
    }, 10);

    // hack to enable clear() to work on p-growl
    this.primeNgMessageService.clearObserver.subscribe(() => {});

    this.userService.isUserImpersonating().then((result) => {
      if (result != null) {
        const userId = result.impersonatedUserGUID;
        this.productAnalyticsService.identifyUser(userId, {
          name: result.impersonatedUserName,
          email: result.impersonatedUserEmail,
          impersonatingUserId: result.isImpersonating ? result.originalUserId : null,
          impersonatingUserName: result.isImpersonating ? result.originalUserName : null,
        });
      }
    });
  }

  onMenuButtonClick(event) {
    this.rotateMenuButton = !this.rotateMenuButton;
    this.topbarMenuActive = false;

    if (this.layoutMode === MenuOrientation.OVERLAY) {
      this.overlayMenuActive = !this.overlayMenuActive;
    } else {
      if (this.isDesktop()) this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
      else this.staticMenuMobileActive = !this.staticMenuMobileActive;
    }

    event.preventDefault();
  }

  onMenuClick($event) {
    this.menuClick = true;
    this.resetMenu = false;

    if (!this.isHorizontal()) {
      setTimeout(() => {
        jQuery(this.layoutMenuScroller).nanoScroller();
      }, 500);
    }
  }

  onTopbarMenuButtonClick(event) {
    this.topbarItemClick = true;
    this.topbarMenuActive = !this.topbarMenuActive;

    if (this.overlayMenuActive || this.staticMenuMobileActive) {
      this.rotateMenuButton = false;
      this.overlayMenuActive = false;
      this.staticMenuMobileActive = false;
    }

    event.preventDefault();
  }

  onTopbarItemClick(event, item) {
    this.topbarItemClick = true;
    if (this.activeTopbarItem === item) this.activeTopbarItem = null;
    else this.activeTopbarItem = item;

    event.preventDefault();
  }

  // todo: check usages and find out what these are doing
  isTablet() {
    let width = window.innerWidth;
    return width <= 1024 && width > 640;
  }

  // todo: check usages and find out what these are doing
  isDesktop() {
    return window.innerWidth > 1024;
  }

  // todo: check usages and find out what these are doing

  isMobile() {
    return window.innerWidth <= 640;
  }

  isOverlay() {
    return this.layoutMode === MenuOrientation.OVERLAY;
  }

  isHorizontal() {
    return this.layoutMode === MenuOrientation.HORIZONTAL;
  }

  changeToStaticMenu() {
    this.layoutMode = MenuOrientation.STATIC;
  }

  changeToOverlayMenu() {
    this.layoutMode = MenuOrientation.OVERLAY;
  }

  changeToHorizontalMenu() {
    this.layoutMode = MenuOrientation.HORIZONTAL;
  }

  private addRouteChangeListener() {
    if (this.routeChange$) {
      this.logger.debug('RouteChange listener already subscribed');
      return;
    }

    this.routeChange$ = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data) {
              return child.snapshot.data;
            } else {
              return null;
            }
          }
          return null;
        }),
      )
      .subscribe((data) => {
        const scrollToTop = data && data.scrollToTop;

        if (!scrollToTop) return;

        this.logger.debug('Scrolling to top on this page');
        try {
          const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
          if (!supportsNativeSmoothScroll) throw new Error('Smooth Scroll not supported.');
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } catch (error) {
          // Failover to implementation without options
          window.scrollTo(0, 0);
        }
      });
  }
}
